import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useError } from '../../../context/ErrorContext';
import Layout from '../../../components/layout/Layout';
import { 
  CreditCard, Search, RefreshCw, ArrowUp, ArrowDown, ChevronRight, ExternalLink, 
  Check, X, Download, Eye, FileText
} from 'lucide-react';
import DateTimeDisplay from '../../../components/common/DateTimeDisplay';
import { PaymentStatus } from '../../subscription/services/PaymentController';
import { billingApi, type AdminPayment } from '../../../api/billingApi';
import PestanasModulo from '../../../components/common/PestanasModulo';
import { PAGOS_TABS } from '../pestanas';

// El adaptador billingApi ya entrega los pagos enriquecidos (sin N+1) y con fechas Date.
// Mantenemos el nombre local `Payment`, sólo ablandamos `metadata` a índice `any`
// para que el JSX siga leyendo receiptUrl/checkoutLink/invoiceId/failReason/mpPaymentId.
type Payment = Omit<AdminPayment, 'metadata'> & {
  metadata: { [key: string]: any };
};

// --- HELPERS ---

const getStatusBadgeClass = (status: PaymentStatus): string => {
  switch (status) {
    case 'pending': return 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-800 dark:text-yellow-300';
    case 'completed': return 'bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-300';
    case 'failed': return 'bg-red-100 dark:bg-red-500/20 text-red-800 dark:text-red-300';
    case 'cancelled': return 'bg-canvas text-content';
    case 'refunded': return 'bg-purple-100 dark:bg-purple-500/20 text-purple-800 dark:text-purple-300';
    default: return 'bg-canvas text-content';
  }
};

const formatMethodName = (method: string): string => {
  switch (method) {
    case 'manual': return 'Transferencia Manual';
    case 'stripe': return 'Tarjeta (Stripe)';
    case 'btcpay': return 'Cripto (BTCPay)';
    case 'mercadopago': return 'Mercado Pago';
    case 'coinpal': return 'Criptomonedas';
    default: return method.charAt(0).toUpperCase() + method.slice(1);
  }
};

const formatStatusName = (status: PaymentStatus): string => {
  switch (status) {
    case 'pending': return 'Pendiente';
    case 'completed': return 'Completado';
    case 'failed': return 'Fallido';
    case 'cancelled': return 'Cancelado';
    case 'refunded': return 'Reembolsado';
    default: return status;
  }
};

// Helper para obtener la URL del comprobante (compatible con ambos campos)
const getReceiptUrl = (metadata: Payment['metadata']): string | null => {
  return metadata.receiptUrl || metadata.fileUrl || null;
};

// Helper para detectar si es PDF
const isPdfUrl = (url: string): boolean => {
  return url.toLowerCase().endsWith('.pdf') || url.toLowerCase().includes('/pdf');
};

// --- MAIN COMPONENT ---

const AdminPaymentsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { addError, handleFirebaseError, clearErrors } = useError();

  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<keyof Payment>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Contar pagos pendientes
  const pendingCount = useMemo(() => {
    return payments.filter(p => p.status === 'pending').length;
  }, [payments]);

  // Fetch payments data
  const fetchPayments = async () => {
    if (!currentUser || currentUser.role !== 'admin') return;

    try {
      setLoading(true);
      clearErrors();

      // Lista ya enriquecida (userName/businessName) y con fechas Date desde la base.
      const payments = await billingApi.adminListPayments();
      setPayments(payments);
    } catch (error) {
      console.error('Error fetching payments:', error);
      handleFirebaseError(error, 'payments-fetch');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [currentUser]);

  // Function to approve a payment
  const handleApprovePayment = async (paymentId: string) => {
    if (!currentUser?.uid) return;
    
    try {
      setProcessingId(paymentId);
      clearErrors();

      const payment = payments.find(p => p.id === paymentId);
      if (!payment) {
        addError('Pago no encontrado', 'not_found', 'payment-approve');
        return;
      }

      // El server activa la suscripción + isPro del tenant.
      await billingApi.approvePayment(paymentId);
      await fetchPayments();

      setSelectedPayment(null);
    } catch (error) {
      console.error('Error approving payment:', error);
      handleFirebaseError(error, 'payment-approve');
    } finally {
      setProcessingId(null);
    }
  };

  // Function to reject a payment
  const handleRejectPayment = async (paymentId: string) => {
    if (!currentUser?.uid) return;
    
    try {
      setProcessingId(paymentId);
      clearErrors();

      await billingApi.rejectPayment(paymentId);
      await fetchPayments();

      setSelectedPayment(null);
    } catch (error) {
      console.error('Error rejecting payment:', error);
      handleFirebaseError(error, 'payment-reject');
    } finally {
      setProcessingId(null);
    }
  };

  const handleSort = (field: keyof Payment) => {
    const newDirection = field === sortField && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
  };

  // Filter and sort payments
  const filteredPayments = useMemo(() => {
    return payments
      .filter(payment => {
        if (!searchTerm) return true;
        const lowerSearch = searchTerm.toLowerCase();
        
        return (
          payment.userName.toLowerCase().includes(lowerSearch) ||
          payment.id.toLowerCase().includes(lowerSearch) ||
          (payment.businessName && payment.businessName.toLowerCase().includes(lowerSearch)) ||
          (payment.metadata.reference && payment.metadata.reference.toLowerCase().includes(lowerSearch))
        );
      })
      .sort((a, b) => {
        let aValue = a[sortField];
        let bValue = b[sortField];
        
        if (aValue instanceof Date && bValue instanceof Date) {
          return sortDirection === 'asc' 
            ? aValue.getTime() - bValue.getTime() 
            : bValue.getTime() - aValue.getTime();
        }
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortDirection === 'asc' 
            ? aValue.localeCompare(bValue) 
            : bValue.localeCompare(aValue);
        }
        
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        }
        
        return 0;
      });
  }, [payments, searchTerm, sortField, sortDirection]);

  const handlePreviewFile = (url: string) => {
    setPreviewUrl(url);
    setShowPreviewModal(true);
  };

  // Render del contenido del modal según el tipo de archivo
  const renderPreviewContent = () => {
    if (!previewUrl) return null;

    if (isPdfUrl(previewUrl)) {
      return (
        <div className="flex flex-col items-center justify-center py-8">
          <FileText className="h-16 w-16 text-red-500 mb-4" />
          <p className="text-content-2 mb-4">El comprobante es un archivo PDF</p>
          <a
            href={previewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-4 py-2 bg-accent-solid text-white rounded-md hover:bg-accent-solid/90"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Abrir PDF en nueva pestaña
          </a>
        </div>
      );
    }

    return (
      <img 
        src={previewUrl} 
        alt="Comprobante de pago" 
        className="max-w-full max-h-[70vh] object-contain"
        onError={(e) => {
          const target = e.target as HTMLImageElement;
          target.style.display = 'none';
          target.parentElement!.innerHTML = '<p class="text-red-500 py-8">Error al cargar la imagen</p>';
        }}
      />
    );
  };

  return (
    <Layout>
      <div className="space-y-6 px-2 sm:px-0">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 animate-fade-in-up" style={{animationDelay: '0.1s'}}>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center">
              <CreditCard className="h-5 w-5 sm:h-6 sm:w-6 mr-2" style={{ color: 'rgb(var(--c-accent))' }} />
              Pagos
              {pendingCount > 0 && (
                <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-500/20 text-yellow-800 dark:text-yellow-300 animate-pulse">
                  {pendingCount} pendiente{pendingCount > 1 ? 's' : ''}
                </span>
              )}
            </h1>
            <p className="mt-1 text-sm text-content-2">
              Gestiona y verifica los pagos de suscripción
            </p>
          </div>
          <button
            onClick={fetchPayments}
            disabled={loading}
            className="inline-flex items-center px-3 py-2 border border-line shadow-sm text-sm leading-4 font-medium rounded-md text-content-2 bg-surface hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent w-full sm:w-auto justify-center sm:justify-start animate-fade-in-up"
            style={{animationDelay: '0.15s'}}
          >
            <RefreshCw className={'h-4 w-4 mr-2 ' + (loading ? 'animate-spin' : '')} />
            {loading ? 'Cargando...' : 'Actualizar'}
          </button>
        </div>

        <PestanasModulo tabs={PAGOS_TABS} activa="pagos" testid="pagos-tabs" />

        {/* Alerta de pagos pendientes */}
        {pendingCount > 0 && (
          <div className="bg-yellow-50 dark:bg-yellow-500/10 border-l-4 border-yellow-400 p-4 rounded-md animate-fade-in-up" style={{animationDelay: '0.15s'}}>
            <div className="flex">
              <div className="flex-shrink-0">
                <CreditCard className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  Tienes <strong>{pendingCount}</strong> pago{pendingCount > 1 ? 's' : ''} pendiente{pendingCount > 1 ? 's' : ''} de revisión.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-surface shadow rounded-lg p-4 animate-fade-in-up" style={{animationDelay: '0.15s'}}>
          <div className="relative flex-grow max-w-lg">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-content-3" />
            </div>
            <input
              type="text"
              className="focus:ring-accent focus:border-blue-500 block w-full pl-10 sm:text-sm border-line rounded-md"
              placeholder="Buscar por cliente, ID o referencia..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Payments Table - Desktop View */}
        <div className="bg-surface shadow overflow-hidden sm:rounded-lg animate-fade-in-up hidden md:block" style={{animationDelay: '0.15s'}}>
          <div className="min-w-full divide-y divide-line">
            <div className="bg-surface-2 px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">
              <span className="font-bold text-sm text-content-2">Pagos de Suscripción</span>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-line">
                <thead className="bg-surface-2">
                  <tr>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('createdAt')}
                    >
                      Fecha
                      {sortField === 'createdAt' && (
                        sortDirection === 'asc' 
                          ? <ArrowUp className="h-4 w-4 inline ml-1" />
                          : <ArrowDown className="h-4 w-4 inline ml-1" />
                      )}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('userName')}
                    >
                      Cliente
                      {sortField === 'userName' && (
                        sortDirection === 'asc' 
                          ? <ArrowUp className="h-4 w-4 inline ml-1" />
                          : <ArrowDown className="h-4 w-4 inline ml-1" />
                      )}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">
                      Monto
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">
                      Método
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('status')}
                    >
                      Estado
                      {sortField === 'status' && (
                        sortDirection === 'asc' 
                          ? <ArrowUp className="h-4 w-4 inline ml-1" />
                          : <ArrowDown className="h-4 w-4 inline ml-1" />
                      )}
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-content-3 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-surface divide-y divide-line">
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-4 whitespace-nowrap text-center text-sm text-content-3">
                        <RefreshCw className="h-5 w-5 animate-spin inline mr-2" />
                        Cargando pagos...
                      </td>
                    </tr>
                  ) : filteredPayments.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-4 whitespace-nowrap text-center text-sm text-content-3">
                        No se encontraron pagos{searchTerm ? ' con ese criterio de búsqueda' : ''}
                      </td>
                    </tr>
                  ) : (
                    filteredPayments.map((payment, index) => {
                      const receiptUrl = getReceiptUrl(payment.metadata);
                      const isPending = payment.status === 'pending';
                      
                      return (
                        <React.Fragment key={payment.id}>
                          <tr 
                            className={
                              'hover:bg-surface-2 cursor-pointer animate-fade-in-up ' +
                              (selectedPayment?.id === payment.id ? 'bg-surface-2 ' : '') +
                              (isPending ? 'bg-yellow-50 dark:bg-yellow-500/10 hover:bg-yellow-100 dark:hover:bg-yellow-500/20 ' : '')
                            }
                            onClick={() => setSelectedPayment(selectedPayment?.id === payment.id ? null : payment)}
                            style={{animationDelay: (0.35 + index * 0.03) + 's'}}
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-content-3">
                              <DateTimeDisplay date={payment.createdAt} format="full" />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-content">{payment.userName}</div>
                                  <div className="text-xs text-content-3">ID: {payment.userId.substring(0, 8)}...</div>
                                  {payment.businessName && (
                                    <div className="text-xs text-content-3">{payment.businessName}</div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-content font-medium">
                              {payment.currency} {payment.amount.toLocaleString('es-MX')}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-content-3">
                              {formatMethodName(payment.method)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={'px-2 inline-flex text-xs leading-5 font-semibold rounded-full ' + getStatusBadgeClass(payment.status)}>
                                {formatStatusName(payment.status)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex justify-end items-center space-x-2">
                                {payment.method === 'manual' && receiptUrl && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handlePreviewFile(receiptUrl);
                                    }}
                                    className="text-accent hover:text-blue-900"
                                    title="Ver comprobante"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                )}
                                {payment.method === 'btcpay' && payment.metadata.checkoutLink && (
                                  <a
                                    href={payment.metadata.checkoutLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-accent hover:text-blue-900"
                                    title="Ver invoice en BTCPay"
                                  >
                                    <ExternalLink className="h-4 w-4" />
                                  </a>
                                )}
                                <ChevronRight
                                  className={'h-5 w-5 text-content-3 transform transition-transform duration-200 ' +
                                    (selectedPayment?.id === payment.id ? 'rotate-90' : '')
                                  } 
                                />
                              </div>
                            </td>
                          </tr>
                          
                          {/* Expanded details */}
                          {selectedPayment?.id === payment.id && (
                            <tr>
                              <td colSpan={6} className="bg-surface-2 px-6 py-4">
                                <div className="space-y-4 animate-fade-in-up" style={{animationDelay: '0.1s'}}>
                                  {/* Payment information */}
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="bg-surface p-4 rounded-md border border-line">
                                      <h4 className="text-sm font-medium text-content mb-2">Información del Pago</h4>
                                      <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                          <span className="text-content-3">ID:</span>
                                          <span className="text-content font-mono text-xs">{payment.id}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-content-3">Plan:</span>
                                          <span className="text-content">{payment.planData.planName}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-content-3">Inicio:</span>
                                          <span className="text-content">
                                            <DateTimeDisplay date={payment.planData.periodStart} format="dateOnly" />
                                          </span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-content-3">Vencimiento:</span>
                                          <span className="text-content">
                                            <DateTimeDisplay date={payment.planData.periodEnd} format="dateOnly" />
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* User information */}
                                    <div className="bg-surface p-4 rounded-md border border-line">
                                      <h4 className="text-sm font-medium text-content mb-2">Información del Cliente</h4>
                                      <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                          <span className="text-content-3">Usuario:</span>
                                          <span className="text-content">{payment.userName}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-content-3">ID Usuario:</span>
                                          <span className="text-content font-mono text-xs">{payment.userId}</span>
                                        </div>
                                        {payment.businessName && (
                                          <div className="flex justify-between">
                                            <span className="text-content-3">Negocio:</span>
                                            <span className="text-content">{payment.businessName}</span>
                                          </div>
                                        )}
                                        {payment.metadata.email && (
                                          <div className="flex justify-between">
                                            <span className="text-content-3">Email:</span>
                                            <span className="text-content">{payment.metadata.email}</span>
                                          </div>
                                        )}
                                        {payment.metadata.whatsapp && (
                                          <div className="flex justify-between">
                                            <span className="text-content-3">WhatsApp:</span>
                                            <span className="text-content">{payment.metadata.whatsapp}</span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* BTCPay specific details */}
                                  {payment.method === 'btcpay' && (
                                    <div className="bg-surface p-4 rounded-md border border-line animate-fade-in-up" style={{animationDelay: '0.15s'}}>
                                      <h4 className="text-sm font-medium text-content mb-2">Detalles del Pago BTCPay</h4>
                                      <div className="space-y-2 text-sm">
                                        {payment.metadata.invoiceId && (
                                          <div className="flex justify-between">
                                            <span className="text-content-3">Invoice ID:</span>
                                            <span className="text-content font-mono text-xs">{payment.metadata.invoiceId}</span>
                                          </div>
                                        )}
                                        {payment.metadata.checkoutLink && (
                                          <div className="flex justify-between items-center">
                                            <span className="text-content-3">Invoice:</span>
                                            <a
                                              href={payment.metadata.checkoutLink}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-accent hover:text-blue-800 flex items-center text-sm"
                                            >
                                              Ver invoice en BTCPay <ExternalLink className="h-3 w-3 ml-1" />
                                            </a>
                                          </div>
                                        )}
                                        {payment.metadata.failReason && (
                                          <div className="flex justify-between">
                                            <span className="text-content-3">Motivo de fallo:</span>
                                            <span className="text-content">{payment.metadata.failReason}</span>
                                          </div>
                                        )}
                                      </div>
                                      <p className="mt-3 pt-3 border-t border-line text-xs text-content-3">
                                        Los pagos BTCPay se confirman y activan automáticamente vía webhook; no requieren aprobación manual.
                                      </p>
                                    </div>
                                  )}

                                  {/* Mercado Pago specific details */}
                                  {payment.method === 'mercadopago' && (
                                    <div className="bg-surface p-4 rounded-md border border-line animate-fade-in-up" style={{animationDelay: '0.15s'}}>
                                      <h4 className="text-sm font-medium text-content mb-2">Detalles del Pago Mercado Pago</h4>
                                      <div className="space-y-2 text-sm">
                                        {payment.metadata.mpPaymentId && (
                                          <div className="flex justify-between">
                                            <span className="text-content-3">ID de pago MP:</span>
                                            <span className="text-content font-mono text-xs">{payment.metadata.mpPaymentId}</span>
                                          </div>
                                        )}
                                        {payment.metadata.preferenceId && (
                                          <div className="flex justify-between">
                                            <span className="text-content-3">Preference:</span>
                                            <span className="text-content font-mono text-xs">{payment.metadata.preferenceId}</span>
                                          </div>
                                        )}
                                        {payment.metadata.checkoutLink && (
                                          <div className="flex justify-between items-center">
                                            <span className="text-content-3">Checkout:</span>
                                            <a
                                              href={payment.metadata.checkoutLink}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-accent hover:text-blue-800 flex items-center text-sm"
                                            >
                                              Abrir checkout <ExternalLink className="h-3 w-3 ml-1" />
                                            </a>
                                          </div>
                                        )}
                                        {payment.metadata.failReason && (
                                          <div className="flex justify-between">
                                            <span className="text-content-3">Motivo de fallo:</span>
                                            <span className="text-content">{payment.metadata.failReason}</span>
                                          </div>
                                        )}
                                      </div>
                                      <p className="mt-3 pt-3 border-t border-line text-xs text-content-3">
                                        Los pagos de Mercado Pago se confirman y activan automáticamente vía webhook; no requieren aprobación manual.
                                      </p>
                                    </div>
                                  )}

                                  {/* Manual payment specific details */}
                                  {payment.method === 'manual' && (
                                    <div className="bg-surface p-4 rounded-md border border-line animate-fade-in-up" style={{animationDelay: '0.15s'}}>
                                      <h4 className="text-sm font-medium text-content mb-2">Detalles del Pago Manual</h4>
                                      <div className="space-y-2">
                                        {receiptUrl && (
                                          <div className="flex justify-between items-center">
                                            <span className="text-sm text-content-3">Comprobante:</span>
                                            <button
                                              onClick={() => handlePreviewFile(receiptUrl)}
                                              className="text-accent hover:text-blue-800 dark:text-blue-300 flex items-center text-sm"
                                            >
                                              {isPdfUrl(receiptUrl) ? (
                                                <>
                                                  <FileText className="h-4 w-4 mr-1" />
                                                  Ver PDF
                                                </>
                                              ) : (
                                                <>
                                                  <Eye className="h-4 w-4 mr-1" />
                                                  Ver imagen
                                                </>
                                              )}
                                              <ExternalLink className="h-3 w-3 ml-1" />
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                      
                                      {payment.status === 'pending' && (
                                        <div className="flex mt-3 pt-3 border-t border-line gap-2">
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleRejectPayment(payment.id);
                                            }}
                                            disabled={processingId !== null}
                                            className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-line shadow-sm text-sm rounded-md text-red-700 dark:text-red-300 bg-surface hover:bg-red-50 dark:hover:bg-red-500/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                                          >
                                            <X className="h-4 w-4 mr-1" />
                                            Rechazar
                                          </button>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleApprovePayment(payment.id);
                                            }}
                                            disabled={processingId !== null}
                                            className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-transparent shadow-sm text-sm rounded-md text-white bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                                          >
                                            <Check className="h-4 w-4 mr-1" />
                                            Aprobar
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Payments Mobile View */}
        <div className="bg-surface shadow rounded-lg overflow-hidden md:hidden animate-fade-in-up" style={{animationDelay: '0.15s'}}>
          <div className="bg-surface-2 px-4 py-3">
            <span className="font-medium text-content-2 text-sm">Pagos de Suscripción</span>
          </div>
          
          <div className="divide-y divide-line">
            {loading ? (
              <div className="p-4 flex justify-center items-center">
                <RefreshCw className="h-5 w-5 animate-spin text-content-3" />
                <span className="ml-2 text-sm text-content-3">Cargando pagos...</span>
              </div>
            ) : filteredPayments.length === 0 ? (
              <div className="p-4 text-center text-sm text-content-3">
                No se encontraron pagos{searchTerm ? ' con ese criterio de búsqueda' : ''}
              </div>
            ) : (
              filteredPayments.map((payment, index) => {
                const receiptUrl = getReceiptUrl(payment.metadata);
                const isPending = payment.status === 'pending';
                
                return (
                  <div 
                    key={payment.id} 
                    className={'animate-fade-in-up ' + (isPending ? 'bg-yellow-50 dark:bg-yellow-500/10' : '')}
                    style={{animationDelay: (0.35 + index * 0.03) + 's'}}
                  >
                    <div 
                      className={'p-4 cursor-pointer ' + (isPending ? 'hover:bg-yellow-100 dark:hover:bg-yellow-500/20' : 'hover:bg-surface-2')}
                      onClick={() => setSelectedPayment(selectedPayment?.id === payment.id ? null : payment)}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="text-sm font-medium text-content">{payment.userName}</div>
                          <div className="text-xs text-content-3 mt-1">
                            <DateTimeDisplay date={payment.createdAt} format="full" />
                          </div>
                          {payment.businessName && (
                            <div className="text-xs text-content-3 mt-1">{payment.businessName}</div>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium">
                            {payment.currency} {payment.amount.toLocaleString('es-MX')}
                          </div>
                          <span className={'mt-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ' + getStatusBadgeClass(payment.status)}>
                            {formatStatusName(payment.status)}
                          </span>
                        </div>
                      </div>
                      
                      <div className="mt-2 flex justify-between text-xs text-content-3">
                        <div>
                          {formatMethodName(payment.method)}
                        </div>
                        <div className="flex items-center">
                          {payment.method === 'manual' && receiptUrl && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePreviewFile(receiptUrl);
                              }}
                              className="text-accent hover:text-blue-900 mr-2"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          )}
                          {payment.method === 'btcpay' && payment.metadata.checkoutLink && (
                            <a
                              href={payment.metadata.checkoutLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-accent hover:text-blue-900 mr-2"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          )}
                          <ChevronRight
                            className={'h-4 w-4 text-content-3 transform transition-transform duration-200 ' +
                              (selectedPayment?.id === payment.id ? 'rotate-90' : '')
                            }
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Expanded details for mobile */}
                    {selectedPayment?.id === payment.id && (
                      <div className="bg-surface-2 p-4 border-t border-line animate-fade-in-up" style={{animationDelay: '0.1s'}}>
                        <div className="space-y-3 text-sm">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <div className="text-content-3">Plan:</div>
                              <div className="font-medium">{payment.planData.planName}</div>
                            </div>
                            <div>
                              <div className="text-content-3">ID:</div>
                              <div className="font-mono text-xs truncate">{payment.id}</div>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <div className="text-content-3">Inicio:</div>
                              <div>
                                <DateTimeDisplay date={payment.planData.periodStart} format="dateOnly" />
                              </div>
                            </div>
                            <div>
                              <div className="text-content-3">Vencimiento:</div>
                              <div>
                                <DateTimeDisplay date={payment.planData.periodEnd} format="dateOnly" />
                              </div>
                            </div>
                          </div>

                          {/* Contacto */}
                          {(payment.metadata.email || payment.metadata.whatsapp) && (
                            <div className="pt-2 border-t border-line">
                              {payment.metadata.email && (
                                <div className="flex justify-between">
                                  <span className="text-content-3">Email:</span>
                                  <span>{payment.metadata.email}</span>
                                </div>
                              )}
                              {payment.metadata.whatsapp && (
                                <div className="flex justify-between">
                                  <span className="text-content-3">WhatsApp:</span>
                                  <span>{payment.metadata.whatsapp}</span>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {payment.method === 'manual' && (
                            <div className="pt-2 border-t border-line">
                              {receiptUrl && (
                                <div className="flex justify-between items-center">
                                  <span className="text-content-3">Comprobante:</span>
                                  <button
                                    onClick={() => handlePreviewFile(receiptUrl)}
                                    className="text-accent flex items-center"
                                  >
                                    {isPdfUrl(receiptUrl) ? 'Ver PDF' : 'Ver imagen'} <ExternalLink className="h-3 w-3 ml-1" />
                                  </button>
                                </div>
                              )}
                            </div>
                          )}

                          {payment.method === 'btcpay' && (
                            <div className="pt-2 border-t border-line space-y-2">
                              {payment.metadata.invoiceId && (
                                <div className="flex justify-between">
                                  <span className="text-content-3">Invoice ID:</span>
                                  <span className="font-mono text-xs truncate ml-2">{payment.metadata.invoiceId}</span>
                                </div>
                              )}
                              {payment.metadata.checkoutLink && (
                                <div className="flex justify-between items-center">
                                  <span className="text-content-3">Invoice:</span>
                                  <a
                                    href={payment.metadata.checkoutLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-accent flex items-center"
                                  >
                                    Ver en BTCPay <ExternalLink className="h-3 w-3 ml-1" />
                                  </a>
                                </div>
                              )}
                              {payment.metadata.failReason && (
                                <div className="flex justify-between">
                                  <span className="text-content-3">Motivo de fallo:</span>
                                  <span>{payment.metadata.failReason}</span>
                                </div>
                              )}
                              <p className="text-xs text-content-3">
                                Confirmación y activación automáticas vía webhook.
                              </p>
                            </div>
                          )}

                          {payment.status === 'pending' && payment.method === 'manual' && (
                            <div className="flex mt-3 pt-3 border-t border-line gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRejectPayment(payment.id);
                                }}
                                disabled={processingId !== null}
                                className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-line shadow-sm text-sm rounded-md text-red-700 dark:text-red-300 bg-surface disabled:opacity-50"
                              >
                                <X className="h-4 w-4 mr-1" />
                                Rechazar
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleApprovePayment(payment.id);
                                }}
                                disabled={processingId !== null}
                                className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-transparent shadow-sm text-sm rounded-md text-white bg-green-600 disabled:opacity-50"
                              >
                                <Check className="h-4 w-4 mr-1" />
                                Aprobar
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreviewModal && previewUrl && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="modal-overlay fixed inset-0 bg-black/40" 
              aria-hidden="true"
              onClick={() => setShowPreviewModal(false)}
            ></div>

            {/* Modal */}
            <div className="inline-block align-bottom bg-surface rounded-lg text-left overflow-hidden shadow-xl sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full modal-panel">
              <div className="bg-surface px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-content" id="modal-title">
                      Vista previa del comprobante
                    </h3>
                    <div className="mt-4 flex justify-center">
                      {renderPreviewContent()}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-surface-2 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-line shadow-sm px-4 py-2 bg-surface text-base font-medium text-content-2 hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowPreviewModal(false)}
                >
                  Cerrar
                </button>
                <a
                  href={previewUrl}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-accent-solid text-base font-medium text-white hover:bg-accent-solid/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent sm:ml-3 sm:w-auto sm:text-sm"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Descargar
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AdminPaymentsPage;